import React from "react";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import { Checkbox } from "../../../components/ui/checkbox";
import { Calendar } from "../../../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Button } from "../../../components/ui/button";
import { cn } from "../../../lib/utils";
import { DynamicField } from "./Types";
import { MultiSelectField } from "./MultiSelectField";

interface DynamicFieldRendererProps {
  field: DynamicField;
  onChange: (fieldId: string, value: string | string[] | number | boolean) => void;
}

export function DynamicFieldRenderer({ field, onChange }: DynamicFieldRendererProps) {
  const { masterMetaDataFieldType, fieldDisplayName, fieldDescription, isRequiredField, fieldValue, id, fieldData } = field;
  const fieldType = masterMetaDataFieldType.frontEndFieldType;

  console.log("value" , fieldValue);
  const handleChange = (value: string | string[] | number | boolean) => {
    onChange(id, value);
  };

  const renderField = () => {
    switch (fieldType) {
      case "text":
        return (
          <Input
            type="text"
            value={fieldValue as string}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={fieldDescription}
            className="border-border"
          />
        );

      case "textarea":
        return (
          <Textarea
            value={fieldValue as string}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={fieldDescription}
            className="border-border min-h-[80px]"
          />
        );

      case "richtext":
        return (
          <Textarea
            value={fieldValue as string}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={fieldDescription}
            className="border-border min-h-[120px]"
          />
        );

      case "number":
        return (
          <Input
            type="number"
            value={fieldValue as string}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={fieldDescription}
            className="border-border"
            step={masterMetaDataFieldType.backendFieldType === "decimal" ? "0.01" : "1"}
          />
        );

      case "checkbox":
        return (
          <div className="flex items-center space-x-2 pt-2">
            <Checkbox
              id={`checkbox-${id}`}
              checked={fieldValue as boolean}
              onCheckedChange={(checked) => handleChange(checked as boolean)}
            />
            <label
              htmlFor={`checkbox-${id}`}
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              {fieldDescription}
            </label>
          </div>
        );

      case "date":
        const dateValue = fieldValue ? new Date(fieldValue as string) : undefined;
        return (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal border-border",
                  !fieldValue && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {dateValue ? format(dateValue, "PPP") : <span>Pick a date</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={dateValue}
                onSelect={(date) => handleChange(date ? date.toISOString() : "")}
                initialFocus
                className="pointer-events-auto"
              />
            </PopoverContent>
          </Popover>
        );

      case "datetime-local":
        return (
          <Input
            type="datetime-local"
            value={fieldValue as string}
            onChange={(e) => handleChange(e.target.value)}
            className="border-border"
          />
        );

      case "select":
        return (
          <Select
            value={fieldValue as string}
            onValueChange={(value) => handleChange(value)}
          >
            <SelectTrigger className="border-border">
              <SelectValue placeholder={`Select ${fieldDisplayName.toLowerCase()}`} />
            </SelectTrigger>
            <SelectContent>
              {fieldData.map((option) => (
                <SelectItem key={option.id} value={option.code}>
                  {option.description}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case "multiselect":
        return (
          <MultiSelectField
            options={fieldData}
            value={Array.isArray(fieldValue) ? fieldValue : []}
            onChange={(value) => handleChange(value)}
            placeholder={`Select ${fieldDisplayName.toLowerCase()}`}
          />
        );

      case "url":
        return (
          <Input
            type="url"
            value={fieldValue as string}
            onChange={(e) => handleChange(e.target.value)}
            placeholder="https://example.com"
            className="border-border"
          />
        );

      default:
        return (
          <Input
            type="text"
            value={fieldValue as string}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={fieldDescription}
            className="border-border"
          />
        );
    }
  };

  // For checkbox, we handle the label differently
  if (fieldType === "checkbox") {
    return (
      <div className="space-y-2">
        <Label className="text-sm font-medium">
          {fieldDisplayName}
          {isRequiredField && <span className="text-primary ml-1">*</span>}
        </Label>
        {renderField()}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-sm font-medium">
        {fieldDisplayName}
        {isRequiredField && <span className="text-primary ml-1">*</span>}
      </Label>
      {renderField()}
    </div>
  );
}
